import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZXN0YXVyYW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg5OTgyMjI5fDA&ixlib=rb-4.1.0&q=85";

const REMEMBER_KEY = "hunter_remember";

function readRemembered() {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY);
    if (!raw) return null;
    const { e, p } = JSON.parse(decodeURIComponent(escape(atob(raw))));
    return { email: e || "", password: p || "" };
  } catch { return null; }
}

function saveRemembered(email, password) {
  try {
    localStorage.setItem(REMEMBER_KEY, btoa(unescape(encodeURIComponent(JSON.stringify({ e: email, p: password })))));
  } catch {}
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  // Pre-fill ONLY when the user explicitly enabled Remember Me previously. Never auto-login.
  useEffect(() => {
    const saved = readRemembered();
    if (saved) {
      setEmail(saved.email);
      setPassword(saved.password);
      setRemember(true);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      if (remember) saveRemembered(email, password);
      else localStorage.removeItem(REMEMBER_KEY);
      navigate("/dashboard");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Hunter" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-sidebar/80" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-96.png" alt="" className="h-10 w-10 rounded-sm" />
            <span className="font-display text-2xl font-bold">Hunter</span>
          </div>
          <div>
            <h1 className="font-display text-4xl font-bold leading-tight tracking-tight lg:text-5xl">
              Run your entire<br />restaurant chain<br /><span className="text-primary">from one place.</span>
            </h1>
            <p className="mt-4 max-w-md text-white/60">Revenue, inventory, procurement, cash, budgets and P&L — unified across every outlet with granular control.</p>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-white/30">Chain Operating System · Phase 1</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <img src="/icons/icon-96.png" alt="" className="mb-3 h-12 w-12 rounded-sm" />
            <span className="font-display text-2xl font-bold">Hunter</span>
          </div>
          <span className="eyebrow">Welcome back</span>
          <h2 className="mt-1 font-display text-3xl font-bold tracking-tight">Sign in</h2>
          <p className="mt-2 text-sm text-muted-foreground">Access your chain dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="eyebrow">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="eyebrow">Password</Label>
              <div className="relative">
                <Input id="password" data-testid="login-password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required className="pr-10" />
                <button type="button" onClick={() => setShowPassword((v) => !v)} data-testid="toggle-password"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="remember" data-testid="remember-me" checked={remember}
                onCheckedChange={(v) => { const on = !!v; setRemember(on); if (!on) localStorage.removeItem(REMEMBER_KEY); }} />
              <Label htmlFor="remember" className="cursor-pointer text-sm font-normal text-muted-foreground">Remember me on this device</Label>
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit">{busy ? "Signing in…" : "Sign in"}</Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
          </div>
          <Button variant="outline" className="w-full" onClick={google} data-testid="google-login">
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
            Continue with Google
          </Button>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            No account? <Link to="/register" className="font-medium text-primary hover:underline" data-testid="to-register">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
